import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const { t } = useI18n();
    const nav = useNavigate();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", payment_status: null, plan_id: null });

    useEffect(() => {
        if (!sessionId) return;
        let attempts = 0;
        const maxAttempts = 10;
        let timer;

        const poll = async () => {
            try {
                const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ status: "paid", payment_status: "paid", plan_id: data.plan_id });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", payment_status: null, plan_id: null });
                    return;
                }
                attempts += 1;
                if (attempts >= maxAttempts) {
                    setState({ status: "timeout", payment_status: data.payment_status, plan_id: data.plan_id });
                    return;
                }
                timer = setTimeout(poll, 2000);
            } catch {
                attempts += 1;
                if (attempts >= maxAttempts) {
                    setState({ status: "error", payment_status: null, plan_id: null });
                } else {
                    timer = setTimeout(poll, 2000);
                }
            }
        };
        poll();
        return () => timer && clearTimeout(timer);
    }, [sessionId]);

    const Body = () => {
        if (state.status === "polling") return (
            <div className="text-center" data-testid="billing-polling">
                <Loader2 className="w-10 h-10 mx-auto text-[#0b905d] animate-spin" />
                <h1 className="font-display text-3xl font-bold text-[#00153f] mt-6">{t("billing_processing")}</h1>
                <p className="text-gray-600 mt-2">{t("billing_processing_sub")}</p>
            </div>
        );
        if (state.status === "paid") return (
            <div className="text-center" data-testid="billing-success">
                <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-7 h-7 text-[#0b905d]" />
                </div>
                <h1 className="font-display text-3xl font-bold text-[#00153f] mt-6">{t("billing_success_title")}</h1>
                <p className="text-gray-600 mt-2">{t("billing_success_sub", { plan: state.plan_id })}</p>
                <Button onClick={() => nav("/app/shops")} className="mt-8 bg-[#0b905d] hover:bg-[#067a4d] text-white" data-testid="goto-dashboard">
                    {t("billing_goto_dashboard")}
                </Button>
            </div>
        );
        return (
            <div className="text-center" data-testid="billing-error">
                <div className="h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
                    <AlertCircle className="w-7 h-7 text-amber-600" />
                </div>
                <h1 className="font-display text-3xl font-bold text-[#00153f] mt-6">
                    {state.status === "expired" ? t("billing_expired") : t("billing_unknown")}
                </h1>
                <p className="text-gray-600 mt-2">{t("billing_try_again")}</p>
                <Link to="/app/billing">
                    <Button className="mt-8 bg-[#00153f] hover:bg-[#001a4d] text-white">{t("billing_back_to_plans")}</Button>
                </Link>
            </div>
        );
    };

    return (
        <div className="min-h-[60vh] flex items-center justify-center px-6">
            <div className="max-w-md w-full bg-white border border-gray-200 rounded-md p-10">
                <Body />
            </div>
        </div>
    );
}
